/**
 * The one thing an upload leaves in browser storage.
 *
 * §2.8 is explicit about the shape of this: persist **only** the minimal
 * identifiers needed to resume an accepted job after a reload, scope them to the
 * signed-in user, and clear them on sign-out. It is equally explicit about what
 * must never be written here — archive bytes, tokens outside Amplify, presigned
 * grants, or journal drafts.
 *
 * So this holds a Job id, the upload it came from, the condition, and the job's
 * idempotency key. The key is here rather than only in memory because the case it
 * exists for is precisely the one that survives a reload: a submission whose
 * response was lost, where replaying the key recovers the same Job and minting a
 * fresh one creates a second.
 *
 * **The grant is not here.** It is a signed, time-limited credential to write to
 * someone else's bucket; a reload that could resume an upload would be a reload
 * that could resume it from another tab, another day, or after a sign-out.
 */

import type { DeviceId, ExperimentLink, PairLink } from './api'

/** Every pointer's storage key starts with this, one per user. */
export const PENDING_JOB_PREFIX = 'brainspot:pending-job:'
const PREFIX = PENDING_JOB_PREFIX

/**
 * Dispatched on `window` after every write and every clear (Sprint 1.9.9 R55).
 *
 * The shell's Home summary reads `/home` once and again only when something
 * changed, and "a recording started" and "a recording finished" are exactly
 * the two moments this pointer is written and cleared: Record and the capture
 * page own both, so announcing them HERE reaches the reader without either page
 * knowing the reader exists. Same-tab only, as a `storage` event is other-tab
 * only; the provider listens for both.
 */
export const PENDING_JOB_EVENT = 'brainspot:pending-job'

function announce(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(PENDING_JOB_EVENT))
}

export interface PendingJob {
  /** Absent when the submission timed out before any response came back. */
  jobId?: string
  uploadId: string
  condition: 'EC' | 'EO'
  /** The `Idempotency-Key` the submission used, so a lost response can replay it. */
  jobKey: string
  /**
   * The `device_id` the submission SENT, and absent when it sent none.
   *
   * The server fingerprints the key over the request body, so a replay must be
   * the same body: a capture that named its headband and is resumed without it
   * is a 409 `idempotency.conflict`, not the job it created. Absent is its own
   * answer (an archive upload, or a pointer from a build before this field) and
   * replays with no `device_id`, never with a default.
   */
  deviceId?: DeviceId
  /**
   * The pair link the submission SENT (the eyes-closed block of a paired
   * session, Sprint 1.9.13), and absent when it sent none. Same rule as
   * `deviceId`: the body is fingerprinted, so a replay must carry it exactly.
   * The eyes-open block writes no pointer: it is sent while block 2 records,
   * and the pointer is for the day's reading.
   */
  pair?: PairLink
  /** Exact experiment link for replaying a submission whose response was lost. */
  experiment?: ExperimentLink
  /** ISO 8601. For "started 4 minutes ago", and to age out a stale pointer. */
  startedAt: string
}

function pairOf(raw: unknown): PairLink | null | undefined {
  if (raw === undefined) return undefined
  if (typeof raw !== 'object' || raw === null) return null
  const p = raw as Partial<PairLink>
  if (typeof p.pairId !== 'string' || p.pairId.length === 0) return null
  if (p.pairRole !== 'EO' && p.pairRole !== 'EC') return null
  if (p.pairIndex !== 1 && p.pairIndex !== 2) return null
  return { pairId: p.pairId, pairRole: p.pairRole, pairIndex: p.pairIndex }
}

const keyFor = (userId: string) => `${PREFIX}${userId}`

function experimentOf(raw: unknown): ExperimentLink | null | undefined {
  if (raw === undefined) return undefined
  if (typeof raw !== 'object' || raw === null) return null
  const link = raw as Partial<ExperimentLink>
  if (typeof link.runId !== 'string' || link.runId.length === 0) return null
  if (link.role !== 'before' && link.role !== 'after') return null
  return { runId: link.runId, role: link.role }
}

/** Every read and write is wrapped: private windows and blocked site data throw. */
function safely<T>(run: () => T, fallback: T): T {
  try {
    return run()
  } catch {
    return fallback
  }
}

export function readPendingJob(userId: string): PendingJob | null {
  return safely(() => {
    const raw = localStorage.getItem(keyFor(userId))
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const candidate = parsed as Partial<PendingJob>
    if (typeof candidate.uploadId !== 'string') return null
    if (typeof candidate.jobKey !== 'string') return null
    if (candidate.condition !== 'EC' && candidate.condition !== 'EO') return null
    if (typeof candidate.startedAt !== 'string') return null
    // A pair link that is present but malformed cannot be replayed as sent, and
    // replaying without it is a 409: such a pointer is no pointer at all.
    const pair = pairOf((parsed as { pair?: unknown }).pair)
    if (pair === null) return null
    const experiment = experimentOf((parsed as { experiment?: unknown }).experiment)
    if (experiment === null || (pair !== undefined && experiment !== undefined)) return null
    return {
      jobId: typeof candidate.jobId === 'string' ? candidate.jobId : undefined,
      uploadId: candidate.uploadId,
      condition: candidate.condition,
      jobKey: candidate.jobKey,
      // Exactly what was written, or nothing: a guessed device would be a
      // different body under the same key.
      ...(typeof candidate.deviceId === 'string' ? { deviceId: candidate.deviceId } : {}),
      ...(pair !== undefined ? { pair } : {}),
      ...(experiment !== undefined ? { experiment } : {}),
      startedAt: candidate.startedAt,
    }
  }, null)
}

export function writePendingJob(userId: string, pending: PendingJob): void {
  safely(() => localStorage.setItem(keyFor(userId), JSON.stringify(pending)), undefined)
  announce()
}

export function clearPendingJob(userId: string): void {
  safely(() => localStorage.removeItem(keyFor(userId)), undefined)
  announce()
}

/**
 * Drop every user's pointer. Called on sign-out.
 *
 * All of them, not just the one that was signed in: a shared machine is how one
 * person's history ends up on another person's screen, and "the user we think is
 * signed in" is the least reliable input available at sign-out time.
 */
export function clearAllPendingJobs(): void {
  safely(() => {
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key !== null && key.startsWith(PREFIX)) doomed.push(key)
    }
    for (const key of doomed) localStorage.removeItem(key)
  }, undefined)
}
